import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import exportValue from "../../apiconfig";

const InvoiceSettings = () => {
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState({
    invoice_month: "",
    financial_year: "",
    prefix: "JP",
    starting_number: "",
    current_number: "",
    initialized: false,
    generated: false,
  });

  useEffect(() => {
    getSettings();
  }, []);

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;

    if (month >= 4) {
      return `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
    }

    return `${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
  };

  const getSettings = async (month, financialYear) => {
    if (!month || !financialYear) return;

    try {
      setLoading(true);

      const response = await axios.post(
        exportValue.host +
          "/" +
          exportValue.version +
          "/" +
          exportValue.api +
          "/get_settings",
        {
          invoice_month: month,
          financial_year: financialYear,
        },
        {
          headers: exportValue.headers,
        }
      );

      if (response.data.status) {
        setSettings((prev) => ({
          ...prev,
          ...response.data.output,
          invoice_month: month,
          financial_year: financialYear,
        }));
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedSettings = {
      ...settings,
      [name]: value,
    };

    setSettings(updatedSettings);

    if (
      (name === "invoice_month" || name === "financial_year") &&
      updatedSettings.invoice_month &&
      updatedSettings.financial_year
    ) {
      getSettings(
        updatedSettings.invoice_month,
        updatedSettings.financial_year
      );
    }
  };

  const saveSettings = async () => {
    if (!settings.invoice_month) {
      Swal.fire({
        icon: "warning",
        title: "Invoice Month Required",
        text: "Please select invoice month.",
      });
      return;
    }

    if (!settings.financial_year) {
      Swal.fire({
        icon: "warning",
        title: "Financial Year Required",
        text: "Please select financial year.",
      });
      return;
    }
    if (!settings.starting_number) {
      Swal.fire({
        icon: "warning",
        title: "Starting Invoice Number Required",
        text: "Please enter the starting invoice number.",
      });
      return;
    }

    const startingNumber = String(settings.starting_number || "").trim();
    if (!/^\d+$/.test(startingNumber)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Invoice Number",
        text: "Starting invoice number should contain only numbers.",
      });
      return;
    }

    if (Number(startingNumber) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Invoice Number",
        text: "Starting invoice number should be greater than zero.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Initialize Invoice Series?",
      text: "Once initialized, the starting invoice number cannot be changed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Initialize",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      const payload = {
        invoice_month: settings.invoice_month,
        financial_year: settings.financial_year,
        starting_number: startingNumber,
      };

      const response = await axios.post(
        exportValue.host +
          "/" +
          exportValue.version +
          "/" +
          exportValue.api +
          "/save_settings",
        payload,
        { headers: exportValue.headers }
      );

      if (response.data.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Invoice Series Initialized Successfully.",
        });

        setSettings({
          ...settings,
          initialized: true,
          current_number: Number(settings.starting_number),
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.message || "Unable to save settings.",
        });
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Server Error",
        text: "Something went wrong.",
      });
    }

    setLoading(false);
  };
  const getFinancialYears = () => {
    const today = new Date();

    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    const startYear = currentMonth >= 4 ? currentYear : currentYear - 1;

    return Array.from({ length: 5 }, (_, index) => {
      const year = startYear + index;

      return `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
    });
  };

  return (
    <div className="container-fluid">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <h5 className="mb-0 fw-bold">Invoice Series Configuration</h5>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Invoice Month</label>

                <input
                  type="month"
                  className="form-control"
                  name="invoice_month"
                  value={settings.invoice_month}
                  onChange={handleChange}
                  disabled={settings.initialized}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Financial Year</label>

                <select
                  className="form-control"
                  name="financial_year"
                  value={settings.financial_year}
                  onChange={handleChange}
                  disabled={settings.initialized}
                >
                  <option value="">Select Financial Year</option>

                  {getFinancialYears().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Invoice Prefix</label>

                <input
                  className="form-control"
                  value={settings.prefix}
                  disabled
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Starting Invoice Number</label>

                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-control"
                  name="starting_number"
                  value={settings.starting_number}
                  onChange={handleChange}
                  disabled={settings.initialized}
                  placeholder="Enter starting invoice number"
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Current Invoice Number</label>

                <input
                  className="form-control"
                  value={
                    settings.current_number
                      ? `${settings.prefix}/${settings.financial_year}/${String(
                          settings.current_number
                        ).padStart(4, "0")}`
                      : "--"
                  }
                  disabled
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Status</label>

                <input
                  className={`form-control ${
                    settings.initialized
                      ? "text-success fw-bold"
                      : "text-danger fw-bold"
                  }`}
                  value={
                    settings.initialized ? "Initialized" : "Not Initialized"
                  }
                  disabled
                />
              </div>
            </div>
          </div>

          <hr />

          <button
            className="btn btn-primary"
            disabled={loading || settings.initialized}
            onClick={saveSettings}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
                Saving...
              </>
            ) : settings.initialized ? (
              "Invoice Series Initialized"
            ) : (
              "Save Configuration"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSettings;
